import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Gift, Copy, Share2, Trophy, Building2, DollarSign,
  Users, TrendingUp, Sparkles, Mail, Twitter, MessageCircle, ArrowRight
} from 'lucide-react';
import api from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { useReferralWebSocket } from '@/hooks/useReferralWebSocket';
import { AffiliateDashboard } from './AffiliateDashboard';
import { ResellerLeaderboard } from './ResellerLeaderboard';
import ResellerDashboard from './ResellerDashboard';

/**
 * Unified Refer & Earn page — Emergent-style consolidation.
 * Replaces 3 separate sidebar entries (Affiliate / Reseller / Leaderboard)
 * with one page that has tabs.
 */
const RefAndEarn = () => {
  const { user, hasFeature } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [referralLink, setReferralLink] = useState('');
  const [stats, setStats] = useState({ total_earnings: 0, pending_payout: 0, total_referrals: 0, conversion_rate: 0 });

  const isReseller = user?.role === 'reseller';
  const isSystemAdmin = user?.role === 'system_admin';
  const showResellerTab = isReseller || isSystemAdmin || hasFeature?.('reseller_portal');

  const initialTab = searchParams.get('tab') || 'overview';
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    setSearchParams({ tab }, { replace: true });
  }, [tab, setSearchParams]);

  // Live "🎉 New signup via your link!" toast when the backend pushes a
  // referral_new_signup event over the analytics WebSocket. Also bump the
  // referral counter optimistically so the top-of-page KPI updates
  // immediately — the affiliate dashboard'll fetch fresh numbers on the
  // next tab switch anyway.
  useReferralWebSocket((payload) => {
    const name = payload.referred_name || payload.referred_email || 'Someone';
    toast.success(`\uD83C\uDF89 New signup via your link!`, {
      description: `${name} just joined Harmonix — commission unlocks the moment they upgrade.`,
      duration: 8000,
    });
    setStats((s) => ({ ...s, total_referrals: (s.total_referrals || 0) + 1 }));
  });

  useEffect(() => {
    (async () => {
      try {
        const [linkRes, dashRes] = await Promise.all([
          api.get('/affiliate/referral-link').catch(() => ({ data: {} })),
          api.get('/affiliate/dashboard').catch(() => ({ data: {} })),
        ]);
        if (linkRes.data?.referral_link) setReferralLink(linkRes.data.referral_link);
        const d = dashRes.data || {};
        setStats({
          total_earnings: d.total_earnings || d.lifetime_earnings || 0,
          pending_payout: d.pending_payout || d.available_balance || 0,
          total_referrals: d.total_referrals || d.referral_count || 0,
          conversion_rate: d.conversion_rate || 0,
        });
      } catch (err) {
        console.error('Failed to load referral overview:', err);
      }
    })();
  }, []);

  const copy = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const shareTwitter = () => {
    const text = encodeURIComponent(`I'm using @harmonix_ai to handle every customer message 24/7 — try it:`);
    const url = encodeURIComponent(referralLink);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
  };

  const shareEmail = () => {
    const subject = encodeURIComponent('Check out Harmonix — AI Employee for service businesses');
    const body = encodeURIComponent(`Hey,\n\nI thought you'd love Harmonix — it answers customer messages, books appointments and qualifies leads on autopilot.\n\nUse my link to get started: ${referralLink}\n`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(`I'm using Harmonix to handle customer messages 24/7. Try it: ${referralLink}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="refer-and-earn-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-500 via-cyan-500 to-emerald-500 p-7 sm:p-9 text-white shadow-xl">
        <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/20 rounded-full blur-3xl" aria-hidden="true" />
        <div className="absolute -bottom-12 -left-12 w-56 h-56 bg-orange-300/30 rounded-full blur-3xl" aria-hidden="true" />

        <div className="relative grid lg:grid-cols-2 gap-6 items-center">
          <div>
            <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur px-3 py-1 rounded-full text-xs font-bold mb-3">
              <Sparkles className="w-3.5 h-3.5" />
              REFER & EARN
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold mb-2 leading-tight">
              Share Harmonix. Earn rewards.
            </h1>
            <p className="text-white/90 text-base mb-5 max-w-md">
              Send your unique link to friends and businesses. Every signup earns you commission, every purchase fuels your tier.
            </p>
            <div className="flex items-center gap-2 bg-white/95 rounded-xl p-1.5 max-w-xl shadow-lg">
              <Input
                readOnly
                value={referralLink || 'Loading…'}
                className="border-0 bg-transparent text-slate-800 font-mono text-sm focus-visible:ring-0"
                data-testid="refer-link-input"
              />
              <Button
                size="sm"
                onClick={() => copy(referralLink, 'Referral link')}
                className="bg-teal-600 hover:bg-teal-700 text-white shrink-0"
                data-testid="refer-link-copy"
              >
                <Copy className="w-4 h-4 mr-1.5" />Copy
              </Button>
            </div>
            <div className="flex items-center gap-2 mt-3.5 text-sm">
              <span className="text-white/80 text-xs mr-1">Share via:</span>
              <button onClick={shareTwitter} aria-label="Share on Twitter" data-testid="share-twitter" className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur flex items-center justify-center transition-colors">
                <Twitter className="w-4 h-4" />
              </button>
              <button onClick={shareWhatsApp} aria-label="Share on WhatsApp" data-testid="share-whatsapp" className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur flex items-center justify-center transition-colors">
                <MessageCircle className="w-4 h-4" />
              </button>
              <button onClick={shareEmail} aria-label="Share via Email" data-testid="share-email" className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur flex items-center justify-center transition-colors">
                <Mail className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <StatCard icon={DollarSign} label="Lifetime earned" value={`$${Number(stats.total_earnings).toFixed(2)}`} />
            <StatCard icon={TrendingUp} label="Pending payout" value={`$${Number(stats.pending_payout).toFixed(2)}`} />
            <StatCard icon={Users} label="Referrals" value={stats.total_referrals} />
            <StatCard icon={Trophy} label="Conversion" value={`${(stats.conversion_rate * 100).toFixed(0)}%`} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-white border border-slate-200 p-1 h-auto flex flex-wrap gap-1 shadow-sm rounded-xl" data-testid="refer-tabs">
          <TabsTrigger value="overview" className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700 px-4 py-2 rounded-lg" data-testid="tab-overview">
            <Gift className="w-4 h-4 mr-1.5" /> Overview
          </TabsTrigger>
          <TabsTrigger value="earnings" className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700 px-4 py-2 rounded-lg" data-testid="tab-earnings">
            <DollarSign className="w-4 h-4 mr-1.5" /> Earnings & Payouts
          </TabsTrigger>
          <TabsTrigger value="leaderboard" className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700 px-4 py-2 rounded-lg" data-testid="tab-leaderboard">
            <Trophy className="w-4 h-4 mr-1.5" /> Leaderboard
          </TabsTrigger>
          {showResellerTab && (
            <TabsTrigger value="reseller" className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700 px-4 py-2 rounded-lg" data-testid="tab-reseller">
              <Building2 className="w-4 h-4 mr-1.5" /> Reseller Hub
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab onSwitchTab={setTab} stats={stats} showResellerTab={showResellerTab} />
        </TabsContent>

        <TabsContent value="earnings" className="mt-6">
          <AffiliateDashboard />
        </TabsContent>

        <TabsContent value="leaderboard" className="mt-6">
          <ResellerLeaderboard />
        </TabsContent>

        {showResellerTab && (
          <TabsContent value="reseller" className="mt-6">
            <ResellerDashboard />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white/15 backdrop-blur border border-white/20 rounded-xl p-3.5">
    <div className="flex items-center gap-2 text-white/80 text-xs mb-1.5">
      <Icon className="w-3.5 h-3.5" />{label}
    </div>
    <div className="text-2xl font-extrabold tabular-nums">{value}</div>
  </div>
);

const OverviewTab = ({ onSwitchTab, stats, showResellerTab }) => (
  <div className="grid md:grid-cols-3 gap-4">
    <Card
      className="bg-white border-slate-200 hover:border-teal-300 hover:shadow-md transition-all cursor-pointer"
      onClick={() => onSwitchTab('earnings')}
      data-testid="overview-card-earnings"
    >
      <CardContent className="p-5">
        <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center mb-3">
          <DollarSign className="w-5 h-5" />
        </div>
        <h3 className="text-base font-bold text-slate-900 mb-1">Earnings & Payouts</h3>
        <p className="text-sm text-slate-600 mb-3">Track every commission, view tier progress, and request payouts.</p>
        <div className="flex items-center justify-between">
          <Badge className="bg-teal-100 text-teal-700 border-0">${Number(stats.pending_payout).toFixed(2)} ready</Badge>
          <ArrowRight className="w-4 h-4 text-slate-400" />
        </div>
      </CardContent>
    </Card>

    <Card
      className="bg-white border-slate-200 hover:border-amber-300 hover:shadow-md transition-all cursor-pointer"
      onClick={() => onSwitchTab('leaderboard')}
      data-testid="overview-card-leaderboard"
    >
      <CardContent className="p-5">
        <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mb-3">
          <Trophy className="w-5 h-5" />
        </div>
        <h3 className="text-base font-bold text-slate-900 mb-1">Leaderboard</h3>
        <p className="text-sm text-slate-600 mb-3">See top affiliates and resellers this month — climb the ranks for bonuses.</p>
        <div className="flex items-center justify-between">
          <Badge className="bg-amber-100 text-amber-700 border-0">Top 100</Badge>
          <ArrowRight className="w-4 h-4 text-slate-400" />
        </div>
      </CardContent>
    </Card>

    {showResellerTab && (
      <Card
        className="bg-white border-slate-200 hover:border-violet-300 hover:shadow-md transition-all cursor-pointer"
        onClick={() => onSwitchTab('reseller')}
        data-testid="overview-card-reseller"
      >
        <CardContent className="p-5">
          <div className="w-10 h-10 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center mb-3">
            <Building2 className="w-5 h-5" />
          </div>
          <h3 className="text-base font-bold text-slate-900 mb-1">Reseller Hub</h3>
          <p className="text-sm text-slate-600 mb-3">Manage white-label clients, custom branding, and bulk billing.</p>
          <div className="flex items-center justify-between">
            <Badge className="bg-violet-100 text-violet-700 border-0">Partner</Badge>
            <ArrowRight className="w-4 h-4 text-slate-400" />
          </div>
        </CardContent>
      </Card>
    )}

    <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200 md:col-span-3">
      <CardContent className="p-5">
        <h3 className="text-base font-bold text-slate-900 mb-1.5 flex items-center gap-1.5">
          <Sparkles className="w-4 h-4 text-orange-500" /> Pro tips to earn more
        </h3>
        <ul className="text-sm text-slate-700 space-y-1.5 mt-2">
          <li className="flex gap-2"><span className="text-teal-500">•</span>Add your link to your email signature — passive but compounding.</li>
          <li className="flex gap-2"><span className="text-teal-500">•</span>Share a real result you achieved with Harmonix on LinkedIn — case studies convert 3× better than generic shoutouts.</li>
          <li className="flex gap-2"><span className="text-teal-500">•</span>Refer agencies and consultants — they often onboard 5+ clients each.</li>
        </ul>
      </CardContent>
    </Card>
  </div>
);

export default RefAndEarn;
